'use es6';

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Seq } from 'immutable';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H3 from 'UIComponents/elements/headings/H3';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getTextColor } from 'MeetingsBase/utils/color';
import Branding from 'MeetingsBase/models/Branding';
import { getHasMoreLinkAvailability } from 'MeetingsLib/selectors/BookInfoSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getMonthButtonOpacity(disabled) {
  return disabled ? '0.5' : 1;
}
function DatePickerHeader({
  startOfMonth,
  availableDateOptions,
  setMonthsAhead,
  isTroubleshooting,
  compact,
  branding,
  isLoadingLinkAvailabilityPage
}) {
  const hasMoreAvailability = useSelector(getHasMoreLinkAvailability);
  const isMinMonth = useMemo(() => {
    return !isTroubleshooting && I18n.moment(availableDateOptions.min()).month() === startOfMonth.month() && I18n.moment(availableDateOptions.min()).year() === startOfMonth.year();
  }, [isTroubleshooting, availableDateOptions, startOfMonth]);
  const isMaxMonth = useMemo(() => {
    return !isTroubleshooting && I18n.moment(availableDateOptions.max()).month() === startOfMonth.month() && I18n.moment(availableDateOptions.max()).year() === startOfMonth.year() && !hasMoreAvailability;
  }, [isTroubleshooting, availableDateOptions, startOfMonth, hasMoreAvailability]);
  const headerClasses = "date-picker-header " + (compact ? 'm-bottom-4' : 'm-bottom-6');
  const monthBackOpacity = getMonthButtonOpacity(isMinMonth);
  const monthForwardOpacity = getMonthButtonOpacity(isMaxMonth);
  const textColor = getTextColor(branding.primaryColor);
  const currentMonthName = startOfMonth.format('MMMM YYYY');
  const previousMonthName = startOfMonth.clone().add(-1, 'months').format('MMMM');
  const nextMonthName = startOfMonth.clone().add(1, 'months').format('MMMM');
  function getPrevMonth() {
    setMonthsAhead(_monthsAhead => _monthsAhead - 1);
  }
  function getNextMonth() {
    setMonthsAhead(_monthsAhead => _monthsAhead + 1);
  }
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    justify: "around",
    className: headerClasses,
    children: [/*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: previousMonthName
        }
      }),
      disabled: !isMinMonth,
      placement: "right",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "transparent",
        "data-test-selenium-test": "previous-month-button",
        onClick: getPrevMonth,
        disabled: isMinMonth,
        "aria-label": I18n.text('prebook.datePicker.previousMonth', {
          month: previousMonthName
        }),
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "left",
          color: textColor,
          style: {
            opacity: monthBackOpacity
          }
        })
      })
    }), /*#__PURE__*/_jsx(H3, {
      "aria-label": currentMonthName,
      "data-selenium-test": "month-and-year",
      "aria-level": 2,
      className: "m-bottom-0",
      style: {
        minWidth: 80,
        textAlign: 'center'
      },
      children: currentMonthName
    }), /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.datePicker.noPreviousMonthReason",
        options: {
          month: nextMonthName
        }
      }),
      disabled: !isMaxMonth || isLoadingLinkAvailabilityPage,
      placement: "left",
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "transparent",
        onClick: getNextMonth,
        disabled: isMaxMonth || isLoadingLinkAvailabilityPage,
        "aria-label": I18n.text('prebook.datePicker.nextMonth', {
          month: nextMonthName
        }),
        "data-selenium-test": "next-month-button",
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "right",
          color: textColor,
          style: {
            opacity: monthForwardOpacity
          }
        })
      })
    })]
  });
}
DatePickerHeader.propTypes = {
  startOfMonth: PropTypes.object.isRequired,
  availableDateOptions: PropTypes.instanceOf(Seq).isRequired,
  setMonthsAhead: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  isTroubleshooting: PropTypes.bool,
  branding: PropTypes.instanceOf(Branding).isRequired,
  isLoadingLinkAvailabilityPage: PropTypes.bool
};
export default DatePickerHeader;